import posthog from "posthog-js";

/**
 * PostHog analytics singleton for accounts.codedesign.ai
 *
 * Mirrors the PostHogAnalytics class used in codedesign-v2 and nocode-frontend.
 * Session recording is enabled with input fields masked for privacy.
 */
class PostHogAnalytics {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = false;
  }

  init() {
    if (typeof window === "undefined") return;

    const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
    const apiHost =
      process.env.REACT_APP_POSTHOG_API_HOST ||
      "https://analytics-proxy-api.codedesign.ai/ph";

    const isDev =
      window.location.host.includes("127.0.0.1") ||
      window.location.host.includes("localhost");

    const forceEnable = process.env.REACT_APP_POSTHOG_FORCE_ENABLE === "true";

    if (!apiKey) {
      console.warn("PostHog API key not found. Analytics disabled.");
      return;
    }

    if (isDev && !forceEnable) {
      console.log("PostHog disabled in development environment");
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        // Cross-domain tracking across *.codedesign.ai
        cross_subdomain_cookie: true,
        // Respect Do Not Track
        respect_dnt: true,
        // Session recording
        session_recording: {
          // Mask all text in input/textarea fields for privacy
          maskAllInputs: true,
          // Mask all text content on the page (passwords, emails, etc.)
          maskTextSelector: "input, textarea",
        },
        // Autocapture pageviews
        capture_pageview: true,
      });

      this.isInitialized = true;
      this.isEnabled = true;
      console.log("PostHog initialized successfully");
    } catch (error) {
      console.error("Failed to initialize PostHog:", error);
    }
  }

  track(eventName, properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;
    try {
      posthog.capture(eventName, {
        ...properties,
        source: "codedesign-authentication-v2",
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("PostHog track error:", error);
    }
  }

  identify(userId, properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;
    try {
      posthog.identify(userId, {
        ...properties,
        source: "codedesign-authentication-v2",
      });
    } catch (error) {
      console.error("PostHog identify error:", error);
    }
  }

  getState() {
    if (!this.isEnabled || !this.isInitialized) return null;
    try {
      return {
        user: {
          userId: posthog.get_distinct_id(),
        },
      };
    } catch (error) {
      console.error("PostHog getState error:", error);
      return null;
    }
  }

  reset() {
    if (!this.isEnabled || !this.isInitialized) return;
    try {
      posthog.reset();
    } catch (error) {
      console.error("PostHog reset error:", error);
    }
  }
}

const posthogAnalytics = new PostHogAnalytics();
export default posthogAnalytics;
